// src/pages/transport-requests/TransportArrivalRequestStepper.tsx
import React, { useMemo, useState } from "react";
import axios from "axios";
import {
    Stepper,
    Group,
    Button,
    Stack,
    Text,
    Alert,
    Paper,
    Divider,
    Select,
    TextInput,
    NumberInput,
    SimpleGrid,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { WHATSAPP_COUNTRIES } from "../../data/whatsappCountries";
import { CreateArrivalRequestDto } from "./types";
import { useParams } from "react-router-dom";
import { useTransportLocations } from "./useTransportLocations"; // ✅ NEW

type Draft = CreateArrivalRequestDto & {
    whatsappCountryIso2?: string; // used by Select
    contactpersonMobile?: string; // optional phone
    note?: string; // optional
};

const PRIORITY_ISO2 = [
    "DK",
    "CA",
    "CN",
    "FR",
    "DE",
    "HK",
    "IN",
    "ID",
    "JP",
    "MY",
    "MX",
    "SG",
    "ES",
    "TW",
    "TH",
    "TR",
    "GB",
    "VN",
    "BG",
];


const MIN_DATE = "2026-04-20";
const MAX_DATE = "2026-05-04";

function isWithinRange(dateStr: string) {
    if (!dateStr) return false;
    // ISO "YYYY-MM-DD" kan sammenlignes som strings
    return dateStr >= MIN_DATE && dateStr <= MAX_DATE;
}
function OptionGrid({
    options,
    value,
    onChange,
    cols,
}: {
    options: { value: string; label: string }[];
    value: string | null;
    onChange: (v: string) => void;
    cols?: { base: number; sm: number };
}) {
    return (
        <SimpleGrid cols={cols ?? { base: 1, sm: 2 }} spacing="sm">
            {options.map((o) => {
                const selected = value === o.value;

                return (
                    <Paper
                        key={o.value}
                        withBorder
                        p="md"
                        radius="md"
                        style={{
                            cursor: "pointer",
                            borderColor: selected ? "var(--mantine-primary-color-filled)" : undefined,
                            boxShadow: selected ? "0 0 0 1px var(--mantine-primary-color-filled)" : undefined,
                        }}
                        onClick={() => onChange(o.value)}
                    >
                        <Text fw={600}>{o.label}</Text>
                    </Paper>
                );
            })}
        </SimpleGrid>
    );
}

export default function TransportArrivalRequestStepper() {
    const [active, setActive] = useState<number>(0);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { eventId } = useParams<{ eventId: string }>();

    const isMobile = useMediaQuery("(max-width: 768px)");

    const [draft, setDraft] = useState<Draft>({
        country: "",
        arrivalDate: "",
        arrivalTime: "",
        arrivalFlightNo: "",
        contactperson: "",
        contactpersonEmail: "",
        contactpersonMobile: "",
        whatsappCountryCode: "",
        whatsappNumber: "",
        numberOfPersons: 1,
        numberOfLuggage: 1,
        dropOffPlace: "",
        note: "",
        whatsappCountryIso2: undefined,
    });


    const { loading: loadingLoc, error: locError, dropoffSuggestions } =
        useTransportLocations(eventId, "dk");

    const dropoffOptions = useMemo(
        () => dropoffSuggestions.map((name) => ({ value: name, label: name })),
        [dropoffSuggestions]
    );

    // Grouped WhatsApp options
    const whatsappCountryOptions = useMemo(() => {
        const prioritySet = new Set(PRIORITY_ISO2);

        const popularItems = WHATSAPP_COUNTRIES.filter((c) => prioritySet.has(c.iso2))
            .sort((a, b) => PRIORITY_ISO2.indexOf(a.iso2) - PRIORITY_ISO2.indexOf(b.iso2))
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        const restItems = WHATSAPP_COUNTRIES.filter((c) => !prioritySet.has(c.iso2))
            .sort((a, b) => a.country.localeCompare(b.country))
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        return [
            { group: "Popular", items: popularItems },
            { group: "All countries", items: restItems },
        ];
    }, []);

    const selectedWhatsappCountry =
        WHATSAPP_COUNTRIES.find((x) => x.iso2 === draft.whatsappCountryIso2)?.country ??
        (draft.whatsappCountryCode ? "Unknown" : null);

    const emailValid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

    const whatsappValid =
        !!draft.whatsappCountryCode &&
        !!draft.whatsappNumber &&
        draft.whatsappNumber.replace(/\s+/g, "").length >= 6;

    const isStepValid = (step: number) => {
        switch (step) {
            case 0:
                return draft.contactperson.trim().length > 0 && emailValid(draft.contactpersonEmail) && whatsappValid;
            case 1:
                return (
                    draft.country.trim().length > 0 &&
                    !!draft.arrivalDate &&
                    isWithinRange(draft.arrivalDate) &&
                    !!draft.arrivalTime &&
                    draft.arrivalFlightNo.trim().length > 0
                );
            case 2:
                return (
                    (draft.numberOfPersons ?? 0) > 0 &&
                    (draft.numberOfLuggage ?? 0) >= 0 &&
                    draft.dropOffPlace.trim().length > 0
                );
            case 3:
                return true;
            default:
                return true;
        }
    };

    const next = () => {
        if (active < 4 && isStepValid(active)) setActive((a) => a + 1);
    };

    const prev = () => setActive((a) => Math.max(0, a - 1));

    const submit = async () => {
        setError(null);

        if (!isStepValid(0) || !isStepValid(1) || !isStepValid(2)) {
            setError("Der mangler oplysninger – gå tilbage og udfyld alle felter.");
            return;
        }
        if (!eventId) {
            setError("EventId mangler i URL.");
            return;
        }

        const toTimeSpan = (t: string) => {
            if (!t) return null;
            return t.length === 5 ? `${t}:00` : t; // "HH:mm" -> "HH:mm:00"
        };

        if (!isWithinRange(draft.arrivalDate)) {
            setError(`Arrival date must be between ${MIN_DATE} and ${MAX_DATE}.`);
            return;
        }

        // Map old arrival fields -> new unified controller fields
        const payload = {
            Kind: 1,
            EventId: Number(eventId),
            Country: draft.country,
            ScheduledDate: draft.arrivalDate,
            ScheduledTime: toTimeSpan(draft.arrivalTime),
            FlightNo: draft.arrivalFlightNo,
            DropOffPlace: draft.dropOffPlace,
            ContactPerson: draft.contactperson,
            ContactPersonEmail: draft.contactpersonEmail,
            ContactPersonMobile: draft.contactpersonMobile || null,
            NumberOfPersons: draft.numberOfPersons,
            NumberOfLuggage: draft.numberOfLuggage,
            Note: draft.note || null,
            WhatsAppCountryCode: draft.whatsappCountryCode,
            WhatsAppNumber: draft.whatsappNumber,
        };

        try {
            setSubmitLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;

            // NEW unified controller endpoint
            console.log("Submitting payload:", payload);
            console.log("eventId:", eventId);
            const res = await axios.post(
                `${apiUrl}/api/v2/transport-requests/event/${eventId}`,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            showNotification({
                title: "Arrival request created",
                message: `Din transport er registreret (ID: ${res.data?.transportRequestId ?? "OK"}).`,
            });

            setActive(4);
        } catch (e: any) {
            console.error("Submit failed:", e);
            console.error("Response status:", e?.response?.status);
            console.error("Response data:", e?.response?.data);

            const apiError =
                typeof e?.response?.data === "string"
                    ? e.response.data
                    : e?.response?.data?.message ||
                    e?.response?.data?.title ||
                    "Kunne ikke oprette transport arrival.";

            setError(apiError);
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Stack>
            {error && (
                <Alert color="red" title="Fejl">
                    {typeof error === "string" ? error : "Fejl ved oprettelse."}
                </Alert>
            )}

            <Stepper
                active={active}
                allowNextStepsSelect={false}
                orientation={isMobile ? "vertical" : "horizontal"}
                size={isMobile ? "sm" : "md"}
            >
                <Stepper.Step label="Contact">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">Contact details</Text>

                        <Stack gap="sm">
                            <TextInput
                                label="Contact person"
                                value={draft.contactperson}
                                onChange={(e) => setDraft((d) => ({ ...d, contactperson: e.target.value }))}
                                required
                            />

                            <TextInput
                                label="Email"
                                value={draft.contactpersonEmail}
                                onChange={(e) => setDraft((d) => ({ ...d, contactpersonEmail: e.target.value }))}
                                required
                                type="email"
                            />

                            <TextInput
                                label="Mobile (optional)"
                                placeholder="e.g. +45 12 34 56 78"
                                value={draft.contactpersonMobile ?? ""}
                                onChange={(e) => setDraft((d) => ({ ...d, contactpersonMobile: e.target.value }))}
                            />

                            <Divider my="xs" />

                            <Text fw={600}>WhatsApp number</Text>

                            <Select
                                label="WhatsApp country"
                                data={whatsappCountryOptions}
                                searchable
                                nothingFoundMessage="No matches"
                                comboboxProps={{ withinPortal: false }}
                                value={draft.whatsappCountryIso2 ?? null}
                                onChange={(iso2) => {
                                    const c = WHATSAPP_COUNTRIES.find((x) => x.iso2 === iso2);
                                    setDraft((d) => ({
                                        ...d,
                                        whatsappCountryIso2: iso2 ?? undefined,
                                        whatsappCountryCode: c?.code ?? "",
                                    }));
                                }}
                                required
                            />

                            {draft.whatsappCountryCode && (
                                <Text size="sm" c="dimmed">
                                    Selected country: <b>{selectedWhatsappCountry ?? "Unknown"}</b>
                                </Text>
                            )}

                            <TextInput
                                label="WhatsApp number"
                                placeholder="e.g. 12345678"
                                value={draft.whatsappNumber ?? ""}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    const cleaned = raw.replace(/[^\d\s-]/g, "");
                                    setDraft((d) => ({ ...d, whatsappNumber: cleaned }));
                                }}
                                required
                            />

                            {!isStepValid(0) && (
                                <Text size="sm" c="red">
                                    Fill in your name, valid email and WhatsApp before you can continue.
                                </Text>
                            )}
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Arrival">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">Arrival details</Text>

                        <Stack gap="sm">
                            <TextInput
                                label="Country"
                                value={draft.country}
                                onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))}
                                required
                            />

                            <TextInput
                                label="Arrival date"
                                type="date"
                                value={draft.arrivalDate}
                                min={MIN_DATE}
                                max={MAX_DATE}
                                onChange={(e) => {
                                    const v = e.target.value;

                                    // hvis tomt (brugeren rydder feltet) -> tillad
                                    if (!v) {
                                        setDraft((d) => ({ ...d, arrivalDate: "" }));
                                        return;
                                    }

                                    // hvis udenfor interval -> ignorer (behold sidste gyldige)
                                    if (!isWithinRange(v)) {
                                        showNotification({
                                            title: "Invalid date",
                                            message: `Please choose a date between ${MIN_DATE} and ${MAX_DATE}.`,
                                        });
                                        return;
                                    }

                                    setDraft((d) => ({ ...d, arrivalDate: v }));
                                }}
                                required
                            />

                            <TextInput
                                label="Arrival time"
                                type="time"
                                value={draft.arrivalTime}
                                onChange={(e) => setDraft((d) => ({ ...d, arrivalTime: e.target.value }))}
                                required
                            />

                            <TextInput
                                label="Flight no"
                                value={draft.arrivalFlightNo}
                                onChange={(e) => setDraft((d) => ({ ...d, arrivalFlightNo: e.target.value }))}
                                required
                            />
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Details">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">Transport details</Text>

                        <Stack gap="sm">
                            <NumberInput
                                label="Number of persons"
                                value={draft.numberOfPersons}
                                min={1}
                                onChange={(v) => {
                                    if (typeof v === "number" && !Number.isNaN(v)) {
                                        setDraft((d) => ({ ...d, numberOfPersons: v }));
                                    }
                                }}
                                required
                            />

                            <NumberInput
                                label="Number of luggage"
                                value={draft.numberOfLuggage}
                                min={0}
                                onChange={(v) => {
                                    if (typeof v === "number" && !Number.isNaN(v)) {
                                        setDraft((d) => ({ ...d, numberOfLuggage: v }));
                                    }
                                }}
                                required
                            />

                            <Divider my="xs" />

                            <Text fw={600} mb="xs">Drop-off place</Text>

                            {locError && (
                                <Alert color="yellow" title="Location warning">
                                    {locError}
                                </Alert>
                            )}

                            {loadingLoc ? (
                                <Text>Loading locations…</Text>
                            ) : dropoffOptions.length === 0 ? (
                                <Alert color="yellow" title="No drop-off locations">
                                    No drop-off locations found for this event.
                                </Alert>
                            ) : (
                                <OptionGrid
                                    options={dropoffOptions}
                                    value={(draft.dropOffPlace ?? "") || null}
                                    onChange={(v) => setDraft((d) => ({ ...d, dropOffPlace: v }))}
                                    cols={{ base: 1, sm: 2 }}
                                />
                            )}

                            {/* Optional note (nice to have) */}
                            <TextInput
                                label="Note (optional)"
                                value={draft.note ?? ""}
                                onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                            />
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Review">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={700}>Review</Text>
                        <Text size="sm" c="dimmed">Check the information before approving.</Text>

                        <Divider my="sm" />

                        <Stack gap={6}>
                            <Text><b>Contact:</b> {draft.contactperson} ({draft.contactpersonEmail})</Text>
                            {draft.contactpersonMobile?.trim() ? (
                                <Text><b>Mobile:</b> {draft.contactpersonMobile}</Text>
                            ) : null}
                            <Text><b>WhatsApp:</b> {draft.whatsappCountryCode} {draft.whatsappNumber}</Text>
                            <Text><b>Country:</b> {draft.country}</Text>
                            <Text><b>Arrival:</b> {draft.arrivalDate} {draft.arrivalTime}</Text>
                            <Text><b>Flight:</b> {draft.arrivalFlightNo}</Text>
                            <Text><b>Persons:</b> {draft.numberOfPersons}</Text>
                            <Text><b>Luggage:</b> {draft.numberOfLuggage}</Text>
                            <Text><b>Drop-off:</b> {draft.dropOffPlace}</Text>
                            {draft.note?.trim() ? <Text><b>Note:</b> {draft.note}</Text> : null}
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Completed>
                    <Paper withBorder p="md" radius="md">
                        <Text fw={700}>Confirmed ✅</Text>

                        <Text>
                            Thank you — your arrival request has been registered.
                        </Text>

                        <Text>
                            WhatsApp: {draft.whatsappCountryCode} {draft.whatsappNumber}
                        </Text>

                        <Text mt="sm">
                            If you need to change or cancel your request, please contact{" "}
                            <Text
                                component="a"
                                href="https://wa.me/4512345678"
                                target="_blank"
                                fw={600}
                                c="green"
                            >
                                +45 12 34 56 78 on WhatsApp
                            </Text>.
                        </Text>
                    </Paper>
                </Stepper.Completed>
            </Stepper>

            {active < 4 && (
                <Group justify="space-between" mt="md">
                    <Button variant="default" onClick={prev} disabled={active === 0}>
                        Back
                    </Button>

                    {active === 3 ? (
                        <Button onClick={submit} loading={submitLoading}>
                            Approve request
                        </Button>
                    ) : (
                        <Button onClick={next} disabled={!isStepValid(active)}>
                            Next
                        </Button>
                    )}
                </Group>
            )}
        </Stack>
    );
}